import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Rss } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import FollowBlock from "@/components/blog/FollowBlock";
import { FeaturedPost, PostRow } from "@/components/blog/PostCards";
import { fetchPosts, RSS_URL } from "@/lib/blog";
import { useSeo } from "@/lib/useSeo";

const SEO = {
  title: "Journal | Anderston Group — Notes on fit-out, craft and AI",
  description:
    "Articles from Anderston Group on commercial interior fit-out, workshop craft, hospitality and workspace design, and AI-led project coordination.",
  path: "/blog",
  image:
    "/img/office.webp",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": "https://anderstongroup.co.uk/blog#blog",
    name: "Anderston Group Journal",
    url: "https://anderstongroup.co.uk/blog",
    description:
      "Notes on interior fit-out, workshop craft and AI in the engine room from Anderston Group.",
    publisher: { "@id": "https://anderstongroup.co.uk/#organization" },
    inLanguage: "en-GB",
  },
};

export default function Blog() {
  useSeo(SEO);
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(false);
  const [category, setCategory] = useState("All");
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    fetchPosts().then(setPosts).catch(() => setError(true));
  }, []);

  useEffect(() => {
    const follow = params.get("follow");
    if (!follow) return;
    const messages = {
      subscribed: ["You're following the journal — thank you.", "success"],
      unsubscribed: ["You've been unsubscribed. Sorry to see you go.", "success"],
      invalid: ["That link is no longer valid.", "error"],
    };
    const [text, kind] = messages[follow] || messages.invalid;
    setTimeout(() => toast[kind](text), 50);
    setParams({}, { replace: true });
  }, [params, setParams]);

  const categories = useMemo(
    () => ["All", ...new Set((posts || []).map((p) => p.category).filter(Boolean))],
    [posts]
  );
  const visible = useMemo(
    () => (posts || []).filter((p) => category === "All" || p.category === category),
    [posts, category]
  );
  const [featured, ...rest] = visible;

  return (
    <main data-testid="blog-page" className="min-h-screen bg-white text-[#0A0A0A]">
      <Navbar />

      <section className="pt-[9rem] lg:pt-[11rem] pb-20 lg:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {/* Header */}
          <div className="grid grid-cols-12 gap-y-6 lg:gap-x-6 pb-12 lg:pb-16">
            <div className="col-span-12 lg:col-span-3">
              <div className="overline">Journal</div>
            </div>
            <div className="col-span-12 lg:col-span-9">
              <h1
                data-testid="blog-heading"
                className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.02] text-balance"
              >
                Notes from the <span className="italic font-normal text-black/55">workshop floor.</span>
              </h1>
              <div className="mt-6 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                <p className="text-base lg:text-lg text-black/65 leading-relaxed max-w-xl">
                  Thinking on interior fit-out, hospitality and workspace design,
                  the craft that happens in the workshop — and what an AI engine
                  room changes about how it all comes together.
                </p>
                <a
                  href={RSS_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="blog-rss-link"
                  className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-black/55 hover:text-black transition-colors shrink-0"
                >
                  <Rss size={12} /> RSS
                </a>
              </div>
            </div>
          </div>

          {/* Category filter */}
          {categories.length > 2 && (
            <div
              data-testid="blog-category-filter"
              className="flex flex-wrap gap-2 pb-12"
            >
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-2 text-xs tracking-[0.18em] uppercase border transition-colors ${
                    category === c
                      ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                      : "hairline text-black/60 hover:text-black hover:border-black/40"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {/* Content */}
          {error && (
            <div data-testid="blog-error" className="py-20 text-center text-black/60">
              The journal is unavailable right now. Please try again shortly.
            </div>
          )}
          {!error && posts === null && <Skeleton />}
          {!error && posts !== null && visible.length === 0 && (
            <div data-testid="blog-empty" className="py-24 border-t hairline">
              <div className="overline">Coming soon</div>
              <p className="mt-4 font-display text-3xl lg:text-4xl tracking-tight max-w-xl">
                The first article is on its way. Follow below to be notified.
              </p>
            </div>
          )}
          {featured && (
            <>
              <FeaturedPost post={featured} />
              {rest.length > 0 && (
                <ul data-testid="blog-post-list" className="mt-16 lg:mt-24 border-t hairline">
                  {rest.map((p, i) => (
                    <PostRow key={p.id} post={p} index={i + 2} />
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </section>

      <FollowBlock />
      <Footer />
    </main>
  );
}

const Skeleton = () => (
  <div data-testid="blog-loading" className="grid grid-cols-12 gap-6 lg:gap-10 border-t hairline pt-8 animate-pulse">
    <div className="col-span-12 lg:col-span-7 aspect-[16/9] bg-black/5" />
    <div className="col-span-12 lg:col-span-5 space-y-4">
      <div className="h-3 w-40 bg-black/5" />
      <div className="h-10 w-4/5 bg-black/5" />
      <div className="h-10 w-3/5 bg-black/5" />
      <div className="h-4 w-full bg-black/5" />
      <div className="h-4 w-2/3 bg-black/5" />
    </div>
  </div>
);
